import { Box, Button, Typography, useTheme } from '@mui/material';

import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import { tokens } from '../../../global/theme/tokens';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import RenderHTML from '../../../components/preview/RenderHTML';
import { renderHours } from '../../time-tracking/worktime';
import MediaPreview from '../../../components/exo/explorer/MediaPreview';
import { useTranslation } from 'react-i18next';
import { TimeTrackingWarningEnumToLabel } from '../../../components/time-tracking/TimeTrackingWarningEnumToLabel';

const warningColorPriority = {
  AUTO_CLOCKOUT: 'error',
  MISSING_CLOCKOUT_IMAGE: 'warning',
  MISSING_COMMENT: 'warning',
};

const TimeEntryCard = ({ entry }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isActive = !entry.timeEnd;
  const totalMinutes = isActive
    ? Math.floor(
        (new Date() - new Date(entry.timeStart)) / (1000 * 60)
      )
    : (moment(entry.timeEnd).utc() - moment(entry.timeStart).utc()) /
      (1000 * 60);

  const warnings = entry.warnings || [];
  const hasWarnings = warnings.length > 0;

  // Determine border color from most severe warning
  const warningColor = hasWarnings
    ? warnings.some((w) => warningColorPriority[w] === 'error')
      ? colors.error
      : colors.warning
    : null;

  return (
    <>
      <Box
        className="flex items-stretch w-full p-3 rounded-lg gap-3 transition-all"
        sx={{
          border: hasWarnings
            ? `1px solid ${warningColor}80`
            : '1px solid transparent',
          bgcolor: hasWarnings ? `${warningColor}30` : 'transparent',
        }}
      >
        <ExoAvatar
          picture={entry.employee?.picture?.temporaryUrl}
          name={`${entry.employee?.firstName} ${entry.employee?.lastName}`}
          size="40"
          onClick={() => navigate(`/employees/${entry.employee?.id}`)}
        />

        <Box className="flex flex-col justify-start w-full gap-1">
          {/* Name and Time Row */}
          <Box className="flex items-center w-full justify-between flex-wrap gap-1">
            <Typography fontSize="14px" fontWeight={600}>
              {entry.employee?.firstName} {entry.employee?.lastName}
            </Typography>

            <Typography
              fontSize="12px"
              fontWeight={600}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: 0.7,
              }}
            >
              <AvTimerIcon sx={{ fontSize: 16 }} />
              {moment(entry.timeStart).format('DD MMM YYYY, HH:mm')}
              {' - '}
              {isActive ? t('Active') : renderHours(totalMinutes) + ' h'}
            </Typography>
          </Box>

          {/* Project & Work Package */}
          {(entry.project || entry.workPackage) && (
            <Box className="flex items-center gap-2 mt-1 flex-wrap">
              {entry.project && (
                <Button
                  sx={{
                    padding: '4px 8px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.info}40`,
                    bgcolor: `${colors.info}15`,
                    ':hover': {
                      borderColor: `${colors.info}80`,
                      bgcolor: `${colors.info}25`,
                    },
                    textTransform: 'none',
                  }}
                  onClick={() =>
                    navigate(`/projects/management/${entry.project.id}`)
                  }
                >
                  <Box className="flex items-center gap-2">
                    {entry.project.image ? (
                      <img
                        src={entry.project.image.temporaryUrl}
                        className="w-7 h-7 object-contain rounded"
                        alt=""
                      />
                    ) : (
                      <ExoAvatar
                        name={entry.project.name}
                        size="28px"
                        type="rounded"
                      />
                    )}
                    <Typography fontSize={12} fontWeight={500} noWrap>
                      {entry.project.name}
                    </Typography>
                  </Box>
                </Button>
              )}
              {entry.workPackage && (
                <Button
                  sx={{
                    padding: '4px 8px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.success}40`,
                    bgcolor: `${colors.success}15`,
                    ':hover': {
                      borderColor: `${colors.success}80`,
                      bgcolor: `${colors.success}25`,
                    },
                    textTransform: 'none',
                  }}
                  onClick={() =>
                    entry.project &&
                    navigate(
                      `/projects/management/${entry.project.id}/work-package/${entry.workPackage.id}`
                    )
                  }
                >
                  <Box className="flex items-center gap-2">
                    {entry.workPackage.icon ? (
                      <img
                        src={entry.workPackage.icon.temporaryUrl}
                        className="w-7 h-7 object-contain rounded"
                        alt=""
                      />
                    ) : (
                      <ExoAvatar
                        name={
                          entry.workPackage.title || entry.workPackage.name
                        }
                        size="28px"
                        type="rounded"
                      />
                    )}
                    <Typography fontSize={12} fontWeight={500} noWrap>
                      {entry.workPackage.title || entry.workPackage.name}
                    </Typography>
                  </Box>
                </Button>
              )}
            </Box>
          )}

          {/* Comment */}
          {entry.comment && (
            <Typography fontSize="12px" variant="h6" sx={{ opacity: 0.8 }}>
              <RenderHTML HTML={entry.comment} />
            </Typography>
          )}

          {/* Warnings */}
          {hasWarnings && (
            <Box className="flex flex-col gap-0.5 mt-1">
              {warnings.map((warning) => (
                <TimeTrackingWarningEnumToLabel
                  key={warning}
                  warning={warning}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Clock-out Photo - full height right side */}
        {entry.clockOutPhoto && (
          <Box
            className="flex-shrink-0 flex items-center"
            sx={{ minWidth: 70 }}
          >
            <MediaPreview
              src={entry.clockOutPhoto.temporaryUrl}
              size="70px"
              allowPreview
            />
          </Box>
        )}
      </Box>

      {/* Divider */}
      <Box
        sx={{ bgcolor: colors.card }}
        className="w-full h-[1px] rounded-full"
      />
    </>
  );
};

export default TimeEntryCard;
